/**
 * useAnalytics — centralised, fire-and-forget funnel event tracking.
 *
 * Funnel stages tracked:
 *   impression       → ActivityCard enters viewport (Intersection Observer)
 *   view             → ActivityPage fully loaded for a tour
 *   availability_open → "Select date" / date-picker opened
 *   checkout_init    → "Book" / navigate to /pay
 *   payment_init     → Checkout page loaded / payment form shown
 *   conversion       → process_tour_booking RPC returned successfully
 */
import { useCallback, useRef } from 'react';
import { supabase, useAuth } from '@mondivago/supabase';
import type { AnalyticsEventType, TrackEventOptions } from '@mondivago/types';
import { logger } from '@/shared/lib/logger';

// ─── Session ID (persisted per browser tab) ──────────────────────────────────
const SESSION_KEY = 'mondivago_session_id';

export function getOrCreateSessionId(): string {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

// ─── Hook ────────────────────────────────────────────────────────────────────
export function useAnalytics() {
  const { user } = useAuth();
  const sessionId = useRef<string>(getOrCreateSessionId()).current;

  /**
   * Fire a single analytics event — non-blocking, but logs errors.
   */
  const track = useCallback(
    (eventType: AnalyticsEventType, options: TrackEventOptions) => {
      const { tourId, blogId, metadata = {} } = options;

      supabase
        .from('app_analytics_events')
        .insert({
          event_type: eventType,
          tour_id: tourId || null,
          blog_id: blogId || null,
          session_id: sessionId,
          user_id: user?.id || null,
          metadata: metadata as any,
        })
        .then(({ error }) => {
          if (error) {
            logger.error(`[Analytics] Failed to track ${eventType}:`, error.message, error.details);
          }
        });
    },
    [sessionId, user?.id]
  );

  /**
   * Returns an IntersectionObserver ref-attach function that fires 'impression'
   * once per mount (deduped via a WeakSet so re-renders don't re-fire).
   */
  const createImpressionRef = useCallback(
    (tourId: string) => {
      const firedRef = useRef(false);

      const refCallback = useCallback(
        (el: HTMLElement | null) => {
          if (!el || firedRef.current) return;

          const observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting && !firedRef.current) {
                  firedRef.current = true;
                  track('impression', { tourId });
                  observer.disconnect();
                }
              });
            },
            { threshold: 0.3 }
          );
          observer.observe(el);
          return () => {
            observer.disconnect();
          };
        },
        [tourId, track]
      );

      return refCallback;
    },
    [track]
  );

  return { track, createImpressionRef };
}

// ─── Standalone impression observer (for non-hook contexts) ──────────────────
/**
 * Attach an IntersectionObserver to an element and fire one 'impression' event.
 * Returns a cleanup function.
 */
export function observeImpression(el: HTMLElement, tourId: string, sessionId: string, userId?: string): () => void {
  let fired = false;
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !fired) {
          fired = true;
          supabase
            .from('app_analytics_events')
            .insert({
              event_type: 'impression',
              tour_id: tourId,
              session_id: sessionId,
              user_id: userId || null,
              metadata: {},
            })
            .then(({ error }) => {
              if (error) {
                logger.error('[Analytics] Failed to track impression (standalone):', error.message);
              }
            });
          observer.disconnect();
        }
      });
    },
    { threshold: 0.3 }
  );
  observer.observe(el);
  return () => {
    observer.disconnect();
  };
}
